import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@apollo/react-hooks";
import { Box, InputAdornment, TextField } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import emailIcon from "@iconify/icons-mdi/email";
import { InlineIcon } from "@iconify/react";
import { MY_LEAVE_UPSERT_EMAILS_BY_COMPANY } from "my-leave/graphql";
import { useMyLeaveEmailsByCompany } from "my-leave/hooks";
import { OcCardButtonBar, OcConfirmationDialog } from "oc/components";
import { useOcProperty, useOcSnackbar } from "oc/hooks";
import useOcCompanies from "oc/hooks/use-companies";

interface MyLeaveEmailByCompanyFormProps {
  type: string;
  setLoading: (loading: boolean) => void;
  emailByCompanyState: any;
  emailByCompany: any;
  isMobile: boolean;
}

export const MyLeaveEmailByCompanyForm = ({
  type,
  setLoading,
  emailByCompanyState,
  emailByCompany,
  isMobile,
}: MyLeaveEmailByCompanyFormProps) => {
  const { t } = useTranslation();
  const { setOpenMessage } = useOcSnackbar();
  type ValueItem = {
    code: string;
    value: string;
    error: boolean;
    errorText: string;
    valid: boolean;
  };
  const [values, setValues] = useState<ValueItem[]>([]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const theme = useTheme();

  const [upsertEmails] = useMutation(MY_LEAVE_UPSERT_EMAILS_BY_COMPANY, {
    onCompleted: () => {
      setLoading(false);
      setOpenMessage({
        type: "success",
        message: t("OC_PROPERTY_SAVE_SUCCEEDED"),
      });
    },
    onError: (error) => {
      console.log(error);
      setLoading(false);
      setOpenMessage({
        type: "error",
        message: t("OC_PROPERTY_SAVE_FAILED"),
      });
    },
  });

  const { data: emailProperty } = useOcProperty({
    resource: "myLeave",
    key: type,
  });

  const { data: emailList } = useMyLeaveEmailsByCompany();
  const { companies } = useOcCompanies();

  type EmailItem = {
    type: string;
    companyCode: string;
    address: string;
    // add other properties if needed
  };

  const emails = (emailList as EmailItem[]).filter(
    (item: EmailItem) => item.type === type,
  );

  const emailsParsed = emails.map((item) => {
    let company = companies.find(
      (companyItem: { code: string; name: string }) =>
        companyItem.code === item.companyCode,
    );
    return {
      code: item.companyCode,
      address: item.address,
      name: company ? company.name : "",
    };
  });

  const list = companies.map((item: { code: string; name: string }) => {
    const isExist = emailsParsed.find(
      (emailItem) => emailItem.code === item.code,
    );
    if (isExist) return isExist;
    return {
      code: item.code,
      address: (emailProperty as string) || "",
      name: item.name,
    };
  });

  const clearValues = (
    list: { code: string; address: string; name: string }[],
  ) => {
    return list.map((item) => {
      const value = {
        code: item.code,
        value: item.address,
        error: false,
        errorText: "",
        valid: false,
      };
      const isExist = values.find((valueItem) => valueItem.code === item.code);
      if (!isExist) {
        setValues((prevState) => [...prevState, value]);
      }
      return item;
    });
  };

  clearValues(list);

  const handleCancel = () => {
    setValues([]);
    clearValues(list);
  };

  const handleChange = (e: { code: string; value: string }) => {
    const newState = [...values];
    for (const item of newState) {
      if (item.code === e.code) {
        item.value = e.value;
        break;
      }
    }
    setValues(newState);
  };

  const clearErrors = (e: {
    code: string;
    error: boolean;
    errorText: string;
  }) => {
    const newState = [...values];
    for (const item of newState) {
      if (item.code === e.code) {
        item.error = e.error;
        item.errorText = e.errorText;
        break;
      }
    }
    setValues(newState);
  };

  const validateEmail = (mail: string) => {
    if (!mail || mail.length > 254) return false;
    const EMAIL_REGEX =
      /^[A-Za-z0-9._%+-]{1,64}@[A-Za-z0-9.-]{1,255}\.[A-Za-z]{2,}$/;

    return EMAIL_REGEX.test(mail);
  };

  const validateValues = () => {
    let isValid = true;
    const newState = [...values];
    for (const item of newState) {
      if (!item.value) {
        isValid = false;
        item.error = true;
        item.errorText = t("OC_ERROR_MESSAGE_EMAIL_IS_REQUIRED");
        continue;
      }
      if (!validateEmail(item.value)) {
        isValid = false;
        item.error = true;
        item.errorText = t("MY_HR_ERROR_MESSAGE_WRONG_EMAIL_FORMAT");
      }
    }
    setValues(newState);
    return isValid;
  };

  const handleSave = () => {
    const isValid = validateValues();
    if (!isValid) {
      setShowConfirmDialog(false);
      return;
    }

    const params = values.map((item) => {
      return {
        companyCode: item.code,
        type: type,
        address: item.value,
      };
    });
    setLoading(true);
    upsertEmails({
      variables: {
        emails: params,
      },
    });
    setShowConfirmDialog(false);
    return;
  };

  let disabled = true;
  if (emailByCompanyState !== emailByCompany) {
    disabled = false;
  }
  if (emailByCompanyState && emailsParsed.length === 0) {
    disabled = false;
  }
  list.map((item: { code: string; address: string; name: string }) => {
    const isChanged = values.find(
      (value) => value.code === item.code && value.value !== item.address,
    );
    if (isChanged) {
      disabled = false;
    }
    return item;
  });

  const handleDialogCancel = () => {
    setShowConfirmDialog(false);
  };

  return (
    <>
      <OcConfirmationDialog
        showDialog={showConfirmDialog}
        onClose={handleSave}
        onCancel={handleDialogCancel}
        title={t("OC_CONFIRM_SETTING")}
        texts={[t("OC_CONFIRM_SETTING_INFO")]}
        okButtonText={t("OC_ALL_RIGHT")}
        cancelButtonText={t("OC_CANCEL")}
      />
      <Box>
        {list.map(
          (
            item: { code: string; address: string; name: string },
            index: number,
          ) => {
            const isLastIndex = index === list.length - 1;
            let valueItem = values.find(
              (valueItem) => valueItem.code === item.code,
            );
            let value = valueItem?.value || "";
            let error = valueItem?.error;
            let errorText = valueItem?.errorText;
            return (
              <Box key={item.code}>
                <TextField
                  style={{
                    width: isMobile ? "100%" : "432px",
                    paddingLeft: isMobile ? "0px" : "16px",
                  }}
                  id={"email-input-field-" + item.code}
                  label={item.code + " " + item.name}
                  variant="outlined"
                  value={value || ""}
                  onChange={(e) =>
                    handleChange({
                      ...valueItem,
                      code: item.code,
                      value: e.target.value,
                    })
                  }
                  error={error}
                  helperText={error && errorText}
                  onFocus={() => {
                    clearErrors({
                      code: item.code,
                      error: false,
                      errorText: "",
                    });
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <InlineIcon
                          icon={emailIcon}
                          height="24px"
                          color={theme.palette.gray60.main}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
                {!isLastIndex && <Box height="36px" />}
              </Box>
            );
          },
        )}
      </Box>
      <OcCardButtonBar
        handleRightButton={handleCancel}
        rightButtonText={t("OC_CANCEL")}
        rightButtonColor="secondary"
        handlePrimaryButton={() => setShowConfirmDialog(true)}
        primaryButtonText={t("OC_SAVE")}
        primaryButtonDisabled={disabled}
      />
    </>
  );
};

export default MyLeaveEmailByCompanyForm;
